'use client';

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import CampoSenha from '../../components/Formulario/CampoSenha';
import AlertBox from '@/components/layout/AlertBox';
import { AnimatedBackground } from '../login/bg/AnimatedBackground';
import useWindowSize from '../../lib/useWindowSize';
import { Link } from '@/components/layout/Link';
import { mdiCheck, mdiClose, mdiLoading, mdiCheckCircle, mdiAlertCircle, mdiPauseCircle, mdiPlayCircle } from '@mdi/js';
import Icon from '@/components/Icon';

type PageState =
    | 'loading'
    | 'expired'
    | 'used'
    | 'invalid'
    | 'form'
    | 'submitting'
    | 'success'
    | 'error';

const RedefinirSenhaPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [width, height, mobile] = useWindowSize();

    const token = searchParams.get('token');

    const [pause, setPause] = useState(false);
    const [state, setState] = useState<PageState>('loading');
    const [email, setEmail] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Validar token ao carregar
    useEffect(() => {
        if (!token) {
            setState('invalid');
            setErrorMessage('Token não fornecido. Use o link enviado por email.');
            return;
        }

        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const response = await axios.get(`/api/password-reset/validate/${token}`);
            if (response.data.ok) {
                setEmail(response.data.email);
                setState('form');
            }
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                const { expired, used } = error.response.data;

                if (expired) {
                    setState('expired');
                } else if (used) {
                    setState('used');
                } else {
                    setState('invalid');
                    setErrorMessage(error.response.data.msg || 'Link inválido');
                }
            } else {
                setState('error');
                setErrorMessage('Erro ao validar link. Tente novamente mais tarde.');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});
        setErrorMessage('');

        // Validação básica
        if (password.length < 8) {
            setFieldErrors({ password: 'Senha deve ter pelo menos 8 caracteres' });
            return;
        }

        if (password !== confirmPassword) {
            setFieldErrors({ confirmPassword: 'Senhas não conferem' });
            return;
        }

        setState('submitting');

        try {
            const response = await axios.post('/api/password-reset/complete', {
                token,
                password,
                confirmPassword,
            });

            if (response.data.ok) {
                setState('success');
            }
        } catch (error: any) {
            setState('form');
            if (axios.isAxiosError(error) && error.response) {
                if (error.response.status === 410) {
                    setState('expired');
                } else if (error.response.data.errors) {
                    // Yup validation errors
                    const errors: Record<string, string> = {};
                    error.response.data.errors.forEach((err: string) => {
                        if (err.includes('Senha')) errors.password = err;
                        if (err.includes('Confirmação')) errors.confirmPassword = err;
                    });
                    setFieldErrors(errors);
                } else {
                    setErrorMessage(error.response.data.msg || 'Erro ao redefinir senha');
                }
            } else {
                setErrorMessage('Erro ao redefinir senha. Tente novamente mais tarde.');
            }
        }
    };

    const renderContent = () => {
        switch (state) {
            case 'loading':
                return (
                    <VBox style={{ textAlign: 'center', gap: 16, padding: 40 }}>
                        <div><Icon path={mdiLoading} size={2} spin color="var(--tc-color-primary)" /></div>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>Validando link...</p>
                    </VBox>
                );

            case 'expired':
                return (
                    <VBox style={{ textAlign: 'center', gap: 16 }}>
                        <div><Icon path={mdiAlertCircle} size={3} color="var(--tc-color-danger)" /></div>
                        <h2 style={{
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '28px',
                            fontWeight: '600',
                            color: 'var(--tc-color-gray-800)'
                        }}>Link Expirado ⏰</h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            Este link de redefinição de senha expirou. Links são válidos por 24 horas.
                        </p>
                        <Botao
                            onClick={() => navigate('/login?esqueci-senha=true')}
                            style={{ marginTop: 24, width: '100%' }}
                            variant="primary"
                        >
                            Solicitar Novo Link
                        </Botao>
                        <div style={{ marginTop: 8 }}>
                            <Link
                                href="/login"
                                className="link-primary"
                            >
                                Voltar para Login
                            </Link>
                        </div>
                    </VBox>
                );

            case 'used':
                return (
                    <VBox style={{ textAlign: 'center', gap: 16 }}>
                        <div><Icon path={mdiCheckCircle} size={3} color="var(--tc-color-success)" /></div>
                        <h2 style={{
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '28px',
                            fontWeight: '600',
                            color: 'var(--tc-color-gray-800)'
                        }}>Link Já Utilizado ✅</h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            Este link já foi utilizado para redefinir sua senha.
                        </p>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            Se você não consegue fazer login, solicite um novo link de redefinição.
                        </p>
                        <Botao
                            onClick={() => navigate('/login')}
                            style={{ marginTop: 24, width: '100%' }}
                            variant="primary"
                        >
                            Fazer Login
                        </Botao>
                        <div style={{ marginTop: 8 }}>
                            <Link
                                href="/login?esqueci-senha=true"
                                className="link-primary"
                            >
                                Solicitar Novo Link
                            </Link>
                        </div>
                    </VBox>
                );

            case 'invalid':
            case 'error':
                return (
                    <VBox style={{ textAlign: 'center', gap: 16 }}>
                        <div><Icon path={mdiClose} size={3} color="var(--tc-color-danger)" /></div>
                        <h2 style={{
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '28px',
                            fontWeight: '600',
                            color: 'var(--tc-color-gray-800)'
                        }}>Link Inválido</h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            {errorMessage || 'Este link é inválido ou não existe.'}
                        </p>
                        <Botao
                            onClick={() => navigate('/login?esqueci-senha=true')}
                            style={{ marginTop: 24, width: '100%' }}
                            variant="primary"
                        >
                            Solicitar Novo Link
                        </Botao>
                        <Link
                            href="/login"
                            className="link-primary"
                        >
                            Voltar para Login
                        </Link>
                    </VBox>
                );

            case 'success':
                return (
                    <VBox style={{ textAlign: 'center', gap: 16 }}>
                        <div><Icon path={mdiCheckCircle} size={3} color="var(--tc-color-success)" /></div>
                        <h2 style={{
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '28px',
                            fontWeight: '600',
                            color: 'var(--tc-color-gray-800)'
                        }}>Senha Redefinida! 🎉</h2>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            Sua senha foi alterada com sucesso.
                        </p>
                        <p style={{
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            margin: 0,
                        }}>
                            Você já pode fazer login com sua nova senha.
                        </p>
                        <Botao
                            onClick={() => navigate('/login')}
                            style={{ marginTop: 24, width: '100%' }}
                            variant="primary"
                        >
                            Ir para Login
                        </Botao>
                    </VBox>
                );

            case 'form':
            case 'submitting':
                return (
                    <>
                        <h2 style={{
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '28px',
                            fontWeight: '600',
                            color: 'var(--tc-color-gray-800)'
                        }}>Redefinir Senha 🔑</h2>
                        <p style={{
                            width: '100%',
                            textAlign: 'center',
                            fontSize: '16px',
                            color: 'var(--tc-color-gray-700)',
                            lineHeight: '1.5',
                            marginBottom: '24px',
                        }}>
                            Digite sua nova senha abaixo.
                        </p>

                        {errorMessage && (
                            <AlertBox variant="danger" style={{ marginBottom: 16 }}>
                                {errorMessage}
                            </AlertBox>
                        )}

                        <form onSubmit={handleSubmit}>
                            <VBox>
                                <CampoSenha
                                    name="password"
                                    label="Nova Senha"
                                    value={password}
                                    onChange={setPassword}
                                    placeholder="Mínimo 8 caracteres"
                                    autoFocus
                                    disabled={state === 'submitting'}
                                    error={fieldErrors.password}
                                    required
                                />

                                <p style={{
                                    fontSize: '13px',
                                    color: 'var(--tc-color-gray-600)',
                                    margin: '-8px 0 0 0',
                                    lineHeight: '1.4',
                                }}>
                                    A senha deve ter pelo menos 8 caracteres, incluindo letra maiúscula, minúscula e número.
                                </p>

                                <CampoSenha
                                    name="confirmPassword"
                                    label="Confirmar Nova Senha"
                                    value={confirmPassword}
                                    onChange={setConfirmPassword}
                                    placeholder="Digite a senha novamente"
                                    disabled={state === 'submitting'}
                                    error={fieldErrors.confirmPassword}
                                    required
                                />

                                <Botao
                                    type="submit"
                                    disabled={
                                        state === 'submitting' || !password || !confirmPassword
                                    }
                                    style={{ width: '100%', marginTop: 8 }}
                                    variant="primary"
                                >
                                    {state === 'submitting' ? (
                                        <HBox gap={8} style={{ justifyContent: 'center' }}>
                                            <div><Icon path={mdiLoading} size={0.8} spin /></div>
                                            Redefinindo...
                                        </HBox>
                                    ) : (
                                        'Redefinir Senha'
                                    )}
                                </Botao>

                                <HBox style={{ justifyContent: 'center', marginTop: 16 }}>
                                    <Link
                                        href="/login"
                                        className="link-primary"
                                    >
                                        Voltar para Login
                                    </Link>
                                </HBox>
                            </VBox>
                        </form>
                    </>
                );
        }
    };

    return (
        <div
            style={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10rem',
            }}
        >
            {!mobile && (
                <img
                    src="/logo-terachat-branco.png"
                    alt="Logo TeraChat"
                    style={{ width: '25%' }}
                />
            )}
            <div>
                <div
                    style={{
                        minWidth: '400px',
                        width: mobile ? '95%' : '25%',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        borderRadius: '24px',
                        padding: '40px',
                        gap: '32px',
                        position: 'relative',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%)',
                    }}
                >
                    <VBox>
                        {mobile && (
                            <div style={{ textAlign: 'center' }}>
                                <img
                                    src="/logo-terachat-branco.png"
                                    alt="Logo TeraChat"
                                    style={{ width: '30%' }}
                                />
                            </div>
                        )}

                        {renderContent()}
                    </VBox>
                </div>
                <div style={{ marginTop: '0.5rem', textAlign: 'center', color: 'var(--tc-color-gray-500)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                    Made with ❤️ by <Link href="https://terachat.com.br" target="_blank">Tera Sistemas</Link>
                </div>
            </div>
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
                {!pause && (
                    <Botao
                        variant="transparent"
                        onClick={() => setPause(true)}
                        icon={mdiPauseCircle}
                        tabIndex={0}
                    >
                        Pausar animação
                    </Botao>
                )}
                {pause && (
                    <Botao
                        variant="transparent"
                        onClick={() => setPause(false)}
                        icon={mdiPlayCircle}
                        tabIndex={0}
                    >
                        Continuar animação
                    </Botao>
                )}
            </div>
            <AnimatedBackground width={width} height={height} pause={pause} />
        </div>
    );
};

export default RedefinirSenhaPage;
